import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { getUserProfile } from "../utils/userUtils";
import { motion, AnimatePresence } from "framer-motion";

function ChatPage() {
  const { id: otherUserId } = useParams();
  const currentUserId = localStorage.getItem("userId");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [otherUser, setOtherUser] = useState(null);
  const [error, setError] = useState("");
  const chatBoxRef = useRef(null);

  const chatId = [currentUserId, otherUserId].sort().join("_");
  const messagesRef = collection(db, "chats", chatId, "messages");

  useEffect(() => {
    const fetchOtherUser = async () => {
      try {
        const data = await getUserProfile(otherUserId);
        setOtherUser(data);

        if (data.allowMessages === false) {
          setError("Bu kullanıcı şu anda mesaj alamıyor.");
        }
      } catch (err) {
        setError(`Kullanıcı bilgileri yüklenirken hata oluştu: ${err.message}. Lütfen sayfayı yenileyin.`);
      }
    };

    fetchOtherUser();
  }, [otherUserId]);

  useEffect(() => {
    const q = query(messagesRef, orderBy("timestamp"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMessages(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      // Mesajlar yüklendiğinde otomatik olarak en alta kaydır
      setTimeout(() => {
        if (chatBoxRef.current) {
          chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
        }
      }, 100);
    }, (err) => {
      setError(`Mesajlar yüklenirken hata oluştu: ${err.message}. Lütfen tekrar deneyin.`);
    });
    return () => unsubscribe();
  }, [chatId]);

  const sendMessage = async () => {
    if (!text.trim()) return;
    try {
      await addDoc(messagesRef, {
        text,
        from: currentUserId,
        to: otherUserId,
        timestamp: serverTimestamp(),
      });
      await updateDoc(doc(db, "chats", chatId), {
        lastMessage: text,
        timestamp: serverTimestamp(),
      });
      setText("");
      // Mesaj gönderildiğinde en alta kaydır
      setTimeout(() => {
        if (chatBoxRef.current) {
          chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
        }
      }, 100);
    } catch (err) {
      setError(`Mesaj gönderilirken hata oluştu: ${err.message}. Lütfen tekrar deneyin.`);
    }
  };

  if (!otherUser) {
    return (
      <motion.div
        style={loadingStyle}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        Yükleniyor...
      </motion.div>
    );
  }

  if (otherUser.allowMessages === false) {
    return (
      <motion.div
        style={loadingStyle}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        ❌ Bu kullanıcı şu anda mesaj alamıyor.
      </motion.div>
    );
  }

  return (
    <motion.div
      style={containerStyle}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.header
        style={headerStyle}
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <h3 style={chatTitleStyle}>💬 {otherUser.username}</h3>
      </motion.header>

      <AnimatePresence>
        {error && (
          <motion.p
            style={errorMessageStyle}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>

      <motion.div
        style={chatBoxStyle}
        ref={chatBoxRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {messages.map((msg, index) => (
          <motion.div
            key={msg.id}
            style={msg.from === currentUserId ? myMsgStyle : otherMsgStyle}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05, duration: 0.3 }}
            whileHover={{ scale: 1.02 }}
          >
            <span style={msgTextStyle}>{msg.text}</span>
            <span style={msgTimestampStyle}>
              {msg.timestamp?.toDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        style={inputContainerStyle}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        <motion.input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Mesaj yaz..."
          style={chatInputStyle}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          whileFocus={{ borderColor: "#00e676", scale: 1.01 }}
          transition={{ duration: 0.2 }}
        />
        <motion.button
          onClick={sendMessage}
          style={sendButtonStyle}
          whileHover={{ scale: 1.05, backgroundColor: "#1e88e5" }}
          whileTap={{ scale: 0.95 }}
          transition={{ duration: 0.2 }}
        >
          Gönder
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  background: "#1a1a1a",
  padding: "20px",
  color: "#fff",
};

const headerStyle = {
  textAlign: "center",
  marginBottom: "15px",
};

const chatTitleStyle = {
  fontSize: "20px",
  fontWeight: "500",
  color: "#00e676",
  margin: 0,
};

const chatBoxStyle = {
  width: "100%",
  maxWidth: "600px",
  height: "400px",
  background: "#2a2a2a",
  borderRadius: "12px",
  padding: "15px",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
  scrollBehavior: "smooth", // Daha pürüzsüz kaydırma için eklendi
};

const myMsgStyle = {
  alignSelf: "flex-end",
  background: "#00e676",
  color: "#000",
  padding: "8px 12px",
  borderRadius: "12px 12px 0 12px",
  maxWidth: "70%",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

const otherMsgStyle = {
  alignSelf: "flex-start",
  background: "#444",
  color: "#fff",
  padding: "8px 12px",
  borderRadius: "12px 12px 12px 0",
  maxWidth: "70%",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

const msgTextStyle = {
  fontSize: "14px",
};

const msgTimestampStyle = {
  fontSize: "10px",
  color: "#aaa",
  alignSelf: "flex-end",
};

const inputContainerStyle = {
  display: "flex",
  gap: "10px",
  width: "100%",
  maxWidth: "600px",
  marginTop: "15px",
};

const chatInputStyle = {
  flex: 1,
  padding: "10px",
  borderRadius: "8px",
  border: "1px solid #444",
  background: "#333",
  color: "#fff",
  fontSize: "14px",
  outline: "none",
};

const sendButtonStyle = {
  background: "#2196f3",
  border: "none",
  borderRadius: "8px",
  padding: "10px 20px",
  fontSize: "14px",
  fontWeight: "500",
  color: "#fff",
  cursor: "pointer",
};

const loadingStyle = {
  textAlign: "center",
  color: "#eee",
  fontSize: "16px",
};

const errorMessageStyle = {
  color: "#f44336",
  textAlign: "center",
  fontSize: "14px",
  marginBottom: "15px",
};

export default ChatPage;